import React, { useMemo, useRef, useState } from 'react';

import type { Category } from '../types/@types/category';
import { CategoryChip, cn } from './ui';

interface CategoryBarProps {
  categories: Category[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
}

type CategoryOption = { id: string | null; name: string; icon?: string };

const STICKY_OFFSET = 'calc(var(--safe-area-top) + 3.25rem)';
const COLLAPSED_MAX_HEIGHT = 'md:max-h-[180px]';
const EXPANDED_MAX_HEIGHT = 'md:max-h-[300px]';

export default function CategoryBar({ categories, activeId, onSelect }: CategoryBarProps) {
  const [expanded, setExpanded] = useState(false);
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const sortedCategories = useMemo(() => {
    // Las categorías "fijadas" (ej. Frecuentes, id que empieza con "__") no se ordenan
    const pinned = categories.filter((c: any) => typeof c.id === 'string' && c.id.startsWith('__'));
    const rest = categories.filter((c: any) => !(typeof c.id === 'string' && c.id.startsWith('__')));
    rest.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return [...pinned, ...rest];
  }, [categories]);

  const items: CategoryOption[] = useMemo(() => {
    return [
      { id: null, name: 'Todas', icon: '' },
      ...sortedCategories,
    ];
  }, [sortedCategories]);

  chipRefs.current.length = items.length;

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!items.length) return;
    const goToIndex = (nextIndex: number) => {
      const item = items[nextIndex];
      onSelect(item.id);
      const target = chipRefs.current[nextIndex];
      target?.focus();
      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        if (window.matchMedia('(max-width: 767px)').matches) {
          target?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
        }
      }
    };

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        event.preventDefault();
        const next = (index + 1) % items.length;
        goToIndex(next);
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        event.preventDefault();
        const prev = (index - 1 + items.length) % items.length;
        goToIndex(prev);
        break;
      }
      case 'Home': {
        event.preventDefault();
        goToIndex(0);
        break;
      }
      case 'End': {
        event.preventDefault();
        goToIndex(items.length - 1);
        break;
      }
      default:
        break;
    }
  };

  const activeIndex = items.findIndex((item) => item.id === activeId);

  return (
    <div
      className="sticky z-20 w-full border-b border-border/60 bg-muted/80 backdrop-blur"
      style={{ top: STICKY_OFFSET }}
    >
      <div className="space-y-2.5 px-4 py-2.5 md:space-y-2 md:p-2.5">
        <div className="hidden items-center justify-end md:flex">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="inline-flex h-8 shrink-0 items-center rounded-full border border-border px-2.5 text-xs font-medium text-text transition hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            {expanded ? 'Ver menos' : 'Ver más'}
          </button>
        </div>
        <div className="relative">
          <div
            role="tablist"
            className={cn(
              'flex gap-2.5 overflow-x-auto overflow-y-hidden whitespace-nowrap rounded-[calc(var(--radius)/1.5)] bg-surface px-4 py-2 shadow-sm md:grid md:grid-cols-[repeat(auto-fit,minmax(160px,1fr))] md:gap-2 md:overflow-x-hidden md:overflow-y-auto md:px-2.5 md:py-2 xl:grid-cols-[repeat(auto-fit,minmax(160px,1fr))] xl:overflow-x-visible',
              expanded ? EXPANDED_MAX_HEIGHT : COLLAPSED_MAX_HEIGHT,
            )}
          >
            {items.map((cat, index) => (
              <CategoryChip
                key={cat.id ?? 'all'}
                ref={(element) => {
                  chipRefs.current[index] = element;
                }}
                label={cat.name}
                icon={cat.icon}
                active={activeIndex === index}
                onClick={() => onSelect(cat.id)}
                onKeyDown={handleKeyDown(index)}
                density="compact"
                className="w-auto md:w-full"
              />
            ))}
          </div>
          <div className="pointer-events-none absolute left-0 top-0 h-full w-6 bg-gradient-to-r from-muted/80 to-transparent md:hidden" />
          <div className="pointer-events-none absolute right-0 top-0 h-full w-6 bg-gradient-to-l from-muted/80 to-transparent md:hidden" />
        </div>
      </div>
    </div>
  );
}
